import React from "react"

import { useToast } from "../../contexts/ToastContext"

const VideoReviewsUploadLinks = ({ videoReviews }) => {
  const { showToast } = useToast()
  const uploadLinks = videoReviews.filter(
    (video) => video.status === "pending" && video.original_filename === "pending_upload" && video.upload_hash
  )

  if (uploadLinks.length === 0) {
    return null
  }

  const copyUploadLink = (hash) => {
    const link = `${window.location.origin}/video-review/upload/${hash}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showToast({ content: "Upload link copied to clipboard!", type: "success" })
      })
      .catch(() => {
        prompt("Copy this upload link:", link)
      })
  }

  const formatExpiryDate = (dateString) => {
    if (!dateString) return "Never"
    const date = new Date(dateString)
    const now = new Date()
    const isExpired = date < now
    const isExpiringSoon = date < new Date(now.getTime() + 24 * 60 * 60 * 1000) // 24 hours

    const formatted = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })

    if (isExpired) return `${formatted} (Expired)`
    if (isExpiringSoon) return `${formatted} (Expires Soon)`
    return formatted
  }

  const getLinkStatus = (video) => {
    if (!video.upload_enabled) return "disabled"

    const expiryDate = new Date(video.upload_expires_at)
    const now = new Date()

    if (expiryDate < now) return "expired"
    if (expiryDate < new Date(now.getTime() + 24 * 60 * 60 * 1000)) return "expiring_soon"
    return "active"
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800"
      case "expiring_soon":
        return "bg-yellow-100 text-yellow-800"
      case "expired":
        return "bg-red-100 text-red-800"
      case "disabled":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Active"
      case "expiring_soon":
        return "Expires Soon"
      case "expired":
        return "Expired"
      case "disabled":
        return "Disabled"
      default:
        return "Unknown"
    }
  }

  return (
    <div className="mb-6 rounded-lg border border-gray-light bg-white shadow-sm">
      <div className="border-b border-gray-light p-6">
        <h5 className="flex items-center text-lg font-semibold text-gray-dark">
          <i className="icon-link mr-2 text-xl text-teal" />
          Active Upload Links ({uploadLinks.length})
        </h5>
        <p className="mt-1 text-sm text-gray">Share these links with clients to upload videos directly</p>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {uploadLinks.map((video) => {
            const linkStatus = getLinkStatus(video)

            return (
              <div key={video.id} className="rounded-lg border border-gray-light p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-3">
                      <span className="font-semibold text-gray-dark">
                        Upload Link #{video.id} (for {video.client?.first_name} {video.client?.last_name})
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusBadge(
                          linkStatus
                        )}`}>
                        {getStatusText(linkStatus)}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-gray">Expires: {formatExpiryDate(video.upload_expires_at)}</div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => copyUploadLink(video.upload_hash)}
                      className="inline-flex items-center rounded-md bg-teal px-3 py-1 text-sm text-white transition-colors hover:bg-teal-dark"
                      title="Copy Upload Link">
                      <i className="icon-copy mr-1" />
                      Copy Link
                    </button>
                  </div>
                </div>

                <div className="rounded bg-gray-50 p-3">
                  <div className="break-all font-mono text-sm text-gray-dark">
                    {`${window.location.origin}/video-review/upload/${video.upload_hash}`}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-6 rounded-lg bg-blue p-4">
          <div className="flex items-start">
            <i className="icon-info mr-3 mt-1 text-gray-dark" />
            <div className="text-sm text-gray-dark">
              <h6 className="mb-1 font-semibold">How to use upload links:</h6>
              <ol className="list-inside list-decimal space-y-1">
                <li>Copy the upload link and share it with your client</li>
                <li>Clients can upload videos directly without creating an account</li>
                <li>Videos are automatically processed and appear in the review queue</li>
                <li>Links expire automatically for security (can be extended if needed)</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoReviewsUploadLinks
